import { useTranslation } from 'react-i18next';
import { ResourceCrudPage, type ResourceListArgs, type PaginatedResult } from '@vipso/shared';
import {
  useListTransportQuery,
  useAddUpdateTransportMutation,
  useDeleteTransportMutation,
} from '../api/enhanceGenerated';
import { useModuleAccess } from '../hooks/useModuleAccess';

type TransportRow = { id: string; name: string; phone?: string };

function useTransportList(args: ResourceListArgs) {
  const result = useListTransportQuery(args);
  const data: PaginatedResult<TransportRow> | undefined = result.data?.listTransport
    ? {
        ...result.data.listTransport,
        rows: result.data.listTransport.rows.map((r) => ({
          id: r.id,
          name: r.name,
          phone: r.phone ?? undefined,
        })),
      }
    : undefined;
  return { ...result, data };
}

export function TransportPage() {
  const { t } = useTranslation();
  const access = useModuleAccess('transport');
  const [save, { isLoading: saving }] = useAddUpdateTransportMutation();
  const [remove] = useDeleteTransportMutation();

  return (
    <ResourceCrudPage<TransportRow>
      title={t('nav.transport')}
      description={t('transport.description')}
      entityLabel={t('transport.entity')}
      entityLabelPlural={t('transport.entityPlural')}
      canCreate={access.canCreate}
      canUpdate={access.canUpdate}
      canDelete={access.canDelete}
      saving={saving}
      refreshLabel={t('common.refresh')}
      useList={useTransportList}
      fields={[
        { key: 'name', label: t('common.name'), required: true },
        { key: 'phone', label: t('common.phone'), type: 'phone' },
      ]}
      onSave={(values, editing) =>
        save({ input: { ...values, id: editing?.id } }).unwrap()
      }
      onDelete={(row) => remove({ id: row.id }).unwrap()}
    />
  );
}
